import types from './types';
import FunnelAPI from '../../../api/funnels';

// Deduplicate concurrent funnels/get calls — reuse in-flight promise
let pendingGetRequest = null;
const CACHE_TTL = 30_000; // 30 seconds
let lastFetchedAt = 0;

export const actions = {
  async get({ commit, rootGetters, state }, { force = false } = {}) {
    // Return cached data if fresh and not forced
    const hasCachedData = Object.keys(state.records).length > 0;
    if (!force && hasCachedData && Date.now() - lastFetchedAt < CACHE_TTL) {
      return Object.values(state.records);
    }

    // Deduplicate: if a request is already in-flight, reuse it
    if (pendingGetRequest) {
      return pendingGetRequest;
    }

    commit(types.SET_FUNNELS_LOADING, true);
    pendingGetRequest = (async () => {
      try {
        const response = await FunnelAPI.get(rootGetters.getCurrentAccountId);
        commit(types.SET_FUNNELS, response.data);
        lastFetchedAt = Date.now();
        return response.data;
      } finally {
        commit(types.SET_FUNNELS_LOADING, false);
        pendingGetRequest = null;
      }
    })();

    return pendingGetRequest;
  },

  async show({ commit }, funnelId) {
    commit(types.SET_FUNNELS_LOADING, true);
    try {
      const response = await FunnelAPI.show(funnelId);
      commit(types.SET_FUNNEL, response.data);
      return response.data;
    } finally {
      commit(types.SET_FUNNELS_LOADING, false);
    }
  },

  async create({ commit }, funnelData) {
    commit(types.SET_FUNNELS_CREATING, true);
    try {
      const response = await FunnelAPI.create(funnelData);
      commit(types.SET_FUNNEL, response.data);
      return response.data;
    } finally {
      commit(types.SET_FUNNELS_CREATING, false);
    }
  },

  async update({ commit }, { funnelId, ...funnelData }) {
    commit(types.SET_FUNNELS_UPDATING, true);
    try {
      const response = await FunnelAPI.update(funnelId, funnelData);
      commit(types.UPDATE_FUNNEL, response.data);
      return response.data;
    } finally {
      commit(types.SET_FUNNELS_UPDATING, false);
    }
  },

  async delete({ commit }, funnelId) {
    commit(types.SET_FUNNELS_DELETING, true);
    try {
      await FunnelAPI.delete(funnelId);
      commit(types.DELETE_FUNNEL, funnelId);
    } finally {
      commit(types.SET_FUNNELS_DELETING, false);
    }
  },

  selectFunnel({ commit }, funnelId) {
    commit(types.SET_SELECTED_FUNNEL, funnelId);
  },

  setSelectedFunnel({ commit }, funnelId) {
    commit(types.SET_SELECTED_FUNNEL, funnelId);
  },

  // Templates
  async getTemplates({ commit }) {
    const response = await FunnelAPI.getTemplates();
    commit(types.SET_FUNNEL_TEMPLATES, response.data);
    return response.data;
  },

  async createFromTemplate({ commit }, { templateType, customizations }) {
    commit(types.SET_FUNNELS_CREATING, true);
    try {
      const response = await FunnelAPI.createFromTemplate(
        templateType,
        customizations
      );
      commit(types.SET_FUNNEL, response.data);
      return response.data;
    } finally {
      commit(types.SET_FUNNELS_CREATING, false);
    }
  },

  // Stage Management
  async addStage({ commit }, { funnelId, stageData }) {
    commit(types.SET_FUNNELS_UPDATING, true);
    try {
      const response = await FunnelAPI.addStage(funnelId, stageData);
      commit(types.ADD_FUNNEL_STAGE, {
        funnelId,
        stageName: stageData.name,
        stageConfig: response.data,
      });
      return response.data;
    } finally {
      commit(types.SET_FUNNELS_UPDATING, false);
    }
  },

  async updateStage({ commit }, { funnelId, stageName, stageData }) {
    commit(types.SET_FUNNELS_UPDATING, true);
    try {
      const response = await FunnelAPI.updateStage(
        funnelId,
        stageName,
        stageData
      );
      commit(types.UPDATE_FUNNEL_STAGE, {
        funnelId,
        stageName,
        stageConfig: response.data,
      });
      return response.data;
    } finally {
      commit(types.SET_FUNNELS_UPDATING, false);
    }
  },

  async removeStage({ commit }, { funnelId, stageName }) {
    commit(types.SET_FUNNELS_UPDATING, true);
    try {
      await FunnelAPI.deleteStage(funnelId, stageName);
      commit(types.REMOVE_FUNNEL_STAGE, { funnelId, stageName });
    } finally {
      commit(types.SET_FUNNELS_UPDATING, false);
    }
  },

  async reorderStages({ commit }, { funnelId, stageOrder }) {
    commit(types.SET_FUNNELS_UPDATING, true);
    try {
      const response = await FunnelAPI.reorderStages(funnelId, stageOrder);
      commit(types.UPDATE_FUNNEL_STAGES, {
        funnelId,
        stages: response.data,
      });
      return response.data;
    } finally {
      commit(types.SET_FUNNELS_UPDATING, false);
    }
  },

  // Analytics
  async getAnalytics(_, { funnelId, params }) {
    const response = await FunnelAPI.getAnalytics(funnelId, params);
    return response.data;
  },

  async getConversionRates(_, { funnelId, params }) {
    const response = await FunnelAPI.getConversionRates(funnelId, params);
    return response.data;
  },

  // Bulk Operations
  async bulkMoveConversations(
    _,
    { conversationIds, targetFunnelId, targetStage }
  ) {
    const response = await FunnelAPI.bulkMoveConversations(
      conversationIds,
      targetFunnelId,
      targetStage
    );
    return response.data;
  },

  // Import/Export
  async exportFunnel(_, { funnelId, format }) {
    const response = await FunnelAPI.exportFunnel(funnelId, format);
    return response.data;
  },

  async importFunnel({ commit }, { funnelData, options }) {
    commit(types.SET_FUNNELS_CREATING, true);
    try {
      const response = await FunnelAPI.importFunnel(funnelData, options);
      commit(types.SET_FUNNEL, response.data);
      return response.data;
    } finally {
      commit(types.SET_FUNNELS_CREATING, false);
    }
  },

  // Duplicate
  async duplicate({ commit }, { funnelId, newName }) {
    commit(types.SET_FUNNELS_CREATING, true);
    try {
      const response = await FunnelAPI.duplicate(funnelId, newName);
      commit(types.SET_FUNNEL, response.data);
      return response.data;
    } finally {
      commit(types.SET_FUNNELS_CREATING, false);
    }
  },
};
